<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :formsConfig="formsConfig"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
  >
    <template #headerContent>
      <GcHeaderContentStatic :title="roleInfo ? roleInfo.RoleName : 'Role'" />
    </template>

    <DxToolbar class="view-toolbar">
      <!-- Role name — always first in toolbar -->
      <DxItem #default location="before" locate-in-menu="never">
        <div class="rp-toolbar-label">
          <span class="rp-toolbar-name" v-if="roleInfo">{{ roleInfo.RoleName }}</span>
          <span class="rp-toolbar-sep" v-if="roleInfo && roleInfo.Description">|</span>
          <span class="rp-toolbar-desc" v-if="roleInfo && roleInfo.Description">{{ roleInfo.Description }}</span>
          <GcInactive v-if="roleInfo" :inactive="roleInfo.Inactive" />
          <span v-if="isSystemOrInternalRole" class="rp-locked-chip">
            <i class="dx-icon-lock" />System
          </span>
        </div>
      </DxItem>
      <DxItem
        :options="toolbar.edit"
        location="after"
        locate-in-menu="auto"
        widget="dxButton"
        :visible="baseView.mode === 'review' && canEdit"
      />
      <DxItem
        :options="toolbar.save"
        location="after"
        locate-in-menu="auto"
        widget="dxButton"
        :visible="baseView.mode !== 'review' && canEdit"
      />
    </DxToolbar>

    <!-- Role metadata edit form — visible in edit mode only, hidden for Admin on System/Internal roles -->
    <div class="rp-meta-form" v-if="baseView.mode !== 'review' && canEdit">
      <div class="rp-meta-field">
        <label class="rp-meta-label">Description</label>
        <DxTextBox
          v-model="roleForm.description"
          styling-mode="outlined"
          placeholder="Optional description"
        />
      </div>
      <div class="rp-meta-field">
        <label class="rp-meta-label"
          >Role Type <span class="rp-meta-required">*</span></label
        >
        <DxSelectBox
          v-if="isInternalAdmin"
          v-model="roleForm.roleType"
          :items="roleTypeItems"
          display-expr="label"
          value-expr="value"
          styling-mode="outlined"
        />
        <DxTextBox
          v-else
          :value="roleForm.roleType"
          styling-mode="outlined"
          :read-only="true"
        />
      </div>
      <div class="rp-meta-field">
        <label class="rp-meta-label"
          >License Type <span class="rp-meta-required">*</span></label
        >
        <DxSelectBox
          v-model="roleForm.licenseType"
          :items="licenseTypeItems"
          display-expr="label"
          value-expr="value"
          styling-mode="outlined"
        />
      </div>
      <div class="rp-meta-field rp-meta-field--toggle">
        <label class="rp-meta-label">Status</label>
        <div class="rp-status-toggle">
          <DxSwitch v-model="roleForm.active" />
          <span class="rp-status-label">{{
            roleForm.active ? "Active" : "Inactive"
          }}</span>
        </div>
      </div>
    </div>

    <!-- rp-layout: exact same structure/CSS as fa-layout in feature-availability.vue -->
    <div class="rp-layout">
      <!-- Left sidebar: Work Centres -->
      <div class="rp-sidebar">
        <div class="rp-sidebar-title">Work Centres</div>
        <div
          v-for="wc in permissionData"
          :key="wc.WorkCentre"
          :class="[
            'rp-wc-nav-item',
            { 'rp-wc-nav-item--active': activeWc === wc.WorkCentre },
          ]"
          @click="scrollToWc(wc.WorkCentre)"
        >
          <DxCheckBox
            :value="wcSidebarValue(wc)"
            @value-changed="onWcSidebarToggle(wc, $event)"
            @click.native="$event.stopPropagation()"
            :read-only="permissionsReadOnly"
          />
          <span class="rp-wc-nav-name">{{ wc.WorkCentre }}</span>
        </div>
      </div>

      <!-- Right content: permission grid -->
      <div class="rp-content">
        <!-- Search -->
        <div class="rp-search-bar">
          <DxTextBox
            v-model="searchQuery"
            placeholder="Search features..."
            :show-clear-button="true"
            styling-mode="outlined"
          />
        </div>

        <!-- 2-column module card grid -->
        <div class="rp-wc-grid" v-if="activeWorkCentre">
          <div
            v-for="mod in visibleModules(activeWorkCentre)"
            :key="mod.Module"
            class="rp-wc-card"
          >
            <!-- Module card header -->
            <div
              class="rp-mod-header rp-mod-header--card"
              @click="
                toggleModuleCollapsed(activeWorkCentre.WorkCentre, mod.Module)
              "
            >
              <i
                :class="
                  isModuleCollapsed(activeWorkCentre.WorkCentre, mod.Module)
                    ? 'dx-icon-chevronnext'
                    : 'dx-icon-chevrondown'
                "
                class="rp-chevron"
              />
              <span class="rp-col-feature rp-mod-title">{{ mod.Module }}</span>
              <div class="rp-agg-inline" @click.stop>
                <span class="rp-col-check">
                  <DxCheckBox
                    :value="modAggValue(mod, 'rw')"
                    @value-changed="onModAggToggle(mod, 'rw', $event)"
                    :read-only="permissionsReadOnly"
                  />
                </span>
                <span class="rp-col-check">
                  <DxCheckBox
                    :value="modAggValue(mod, 'ro')"
                    @value-changed="onModAggToggle(mod, 'ro', $event)"
                    :read-only="permissionsReadOnly"
                  />
                </span>
                <span class="rp-col-check">
                  <DxCheckBox
                    :value="modAggValue(mod, 'none')"
                    @value-changed="onModAggToggle(mod, 'none', $event)"
                    :read-only="permissionsReadOnly"
                  />
                </span>
              </div>
            </div>

            <!-- Column header row -->
            <div class="rp-col-header-row">
              <span class="rp-col-feature"></span>
              <span class="rp-col-check rp-col-label">Read / Write</span>
              <span class="rp-col-check rp-col-label">Read Only</span>
              <span class="rp-col-check rp-col-label">No Access</span>
            </div>

            <!-- Feature rows -->
            <div
              v-if="!isModuleCollapsed(activeWorkCentre.WorkCentre, mod.Module)"
            >
              <div
                v-for="feat in visibleFeatures(mod)"
                :key="feat.FeatureCode"
                :class="[
                  'rp-feature-row',
                  { 'rp-feature-row--global': feat.IsGlobal },
                ]"
              >
                <span class="rp-col-feature">
                  {{ feat.DisplayName }}
                  <span v-if="feat.IsGlobal" class="rp-global-badge"
                    >Global</span
                  >
                </span>
                <span class="rp-col-check">
                  <DxCheckBox
                    v-if="feat.HasReadWritePermission && !feat.IsGlobal"
                    :value="(permState[feat.FeatureCode] || 'none') === 'rw'"
                    @value-changed="
                      $event.event &&
                        $event.value &&
                        setPermission(feat.FeatureCode, 'rw')
                    "
                    :read-only="permissionsReadOnly"
                  />
                  <span v-else class="rp-no-option" />
                </span>
                <span class="rp-col-check">
                  <DxCheckBox
                    v-if="!feat.IsGlobal"
                    :value="(permState[feat.FeatureCode] || 'none') === 'ro'"
                    @value-changed="
                      $event.event &&
                        $event.value &&
                        setPermission(feat.FeatureCode, 'ro')
                    "
                    :read-only="permissionsReadOnly"
                  />
                  <span v-else class="rp-global-lock">Always On</span>
                </span>
                <span class="rp-col-check">
                  <DxCheckBox
                    v-if="!feat.IsGlobal"
                    :value="(permState[feat.FeatureCode] || 'none') === 'none'"
                    @value-changed="
                      $event.event &&
                        $event.value &&
                        setPermission(feat.FeatureCode, 'none')
                    "
                    :read-only="permissionsReadOnly"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="
            !formsConfig.loadingVisible &&
            activeWorkCentre &&
            visibleModules(activeWorkCentre).length === 0
          "
          class="rp-empty"
        >
          No features match your search.
        </div>
        <div
          v-if="!formsConfig.loadingVisible && permissionData.length === 0"
          class="rp-empty"
        >
          No permissions data found for this role.
        </div>
      </div>
    </div>
  </gc-main-layout>
</template>

<script>
import BaseViewMixin from "../../../v1/_mixins/base.view";
import { RolePermissionService } from "../../../_services/api/settings/graycorp/role-permission.service";
import { TokenStorage, FeatureStorage } from "../../../_services/storage";


export default {
  mixins: [BaseViewMixin],
  data() {
    return {
      roleInfo: null,
      roleTypeItems: [],
      licenseTypeItems: [],
      roleForm: {
        description: "",
        roleType: "",
        licenseType: "",
        active: true,
      },
      permissionData: [],
      permState: {},
      modCollapsed: {},
      searchQuery: "",
      activeWc: null,
      customerCode: TokenStorage.getTenant(),

      toolbar: {
        edit: {
          icon: "edit",
          onClick: () => {
            this.$router.push({
              path: `/role-permission/edit/${this.baseView.id}`,
            });
          },
        },
        save: {
          icon: "floppy",
          onClick: () => {
            this.$logger.trackEventButtonClickSave();
            this.saveAll();
          },
        },
      },
    };
  },
  computed: {
    activeWorkCentre() {
      return (
        this.permissionData.find((wc) => wc.WorkCentre === this.activeWc) ||
        null
      );
    },
    isInternalAdmin() {
      return FeatureStorage.getUserType() === "Internal Administrator";
    },
    isSystemOrInternalRole() {
      return (
        this.roleInfo &&
        (this.roleInfo.RoleType === "System" ||
          this.roleInfo.RoleType === "Internal")
      );
    },
    permissionsReadOnly() {
      return (
        this.baseView.mode === "review" ||
        (!this.isInternalAdmin && this.isSystemOrInternalRole)
      );
    },
    canEdit() {
      return this.isInternalAdmin || !this.isSystemOrInternalRole;
    },
  },
  methods: {
    // ── Data loading ────────────────────────────────────────────────

    load() {
      const roleId = this.baseView.id;
      if (!roleId || !this.customerCode) return;
      this.showLoading(true);
      Promise.all([
        RolePermissionService.getRoleById(roleId),
        RolePermissionService.getRolePermissions(roleId, this.customerCode),
        RolePermissionService.getRoleTypes(),
        RolePermissionService.getLicenseTypes(),
      ])
        .then(([roleResp, permResp, roleTypesResp, licenseTypesResp]) => {
          this.roleTypeItems = (roleTypesResp.data.value || []).map((t) => ({
            label: t.DisplayName,
            value: t.Code,
          }));
          this.licenseTypeItems = (licenseTypesResp.data.value || [])
            .filter((t) => t.Inactive !== "Y")
            .map((t) => ({ label: t.DisplayName, value: t.Code }));

          this.roleInfo = roleResp.data || null;
          if (this.roleInfo) {
            this.roleForm = {
              description: this.roleInfo.Description || "",
              roleType: this.roleInfo.RoleType || "",
              licenseType: this.roleInfo.LicenseType || "",
              active: this.roleInfo.Inactive !== "Y",
            };
          }
          this.permissionData = permResp.data;
          if (this.permissionData.length) {
            this.activeWc = this.permissionData[0].WorkCentre;
          }
          const state = {};
          for (const wc of this.permissionData) {
            for (const mod of wc.Modules) {
              for (const feat of mod.Features) {
                if (feat.IsGlobal) {
                  state[feat.FeatureCode] = "ro";
                } else if (feat.CanRead && feat.CanWrite) {
                  state[feat.FeatureCode] = "rw";
                } else if (feat.CanRead) {
                  state[feat.FeatureCode] = "ro";
                } else {
                  state[feat.FeatureCode] = "none";
                }
              }
            }
          }
          this.permState = state;
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to load role permissions");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },

    // ── Save ─────────────────────────────────────────────────────────

    saveAll() {
      const roleId = this.baseView.id;
      if (!roleId) return;

      if (!this.roleForm.licenseType) {
        this.$notify("License type is required", "error");
        return;
      }

      this.showLoading(true);
      const changes = Object.entries(this.permState).map(
        ([featureCode, level]) => ({
          FeatureCode: featureCode,
          CanRead: level !== "none",
          CanWrite: level === "rw",
        }),
      );

      RolePermissionService.updateRole(roleId, {
        Description: this.roleForm.description || null,
        RoleType: this.roleForm.roleType,
        LicenseType: this.roleForm.licenseType,
        Inactive: this.roleForm.active ? "N" : "Y",
      })
        .then(() =>
          RolePermissionService.saveRolePermissions(roleId, changes),
        )
        .then(() => {
          this.$notify("Role saved successfully", "success");
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({ path: `/role-permission/review/${roleId}` });
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to save role");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },

    // ── Navigation ────────────────────────────────────────────────────

    backButtonFunction() {
      this.$router.push({ path: "/role-permission" });
    },

    scrollToWc(wcName) {
      this.activeWc = wcName;
    },

    // ── Search / Visibility ───────────────────────────────────────────

    visibleModules(wc) {
      return wc.Modules.filter((mod) => this.visibleFeatures(mod).length > 0);
    },

    visibleFeatures(mod) {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return mod.Features;
      return mod.Features.filter((f) =>
        f.DisplayName.toLowerCase().includes(q),
      );
    },

    // ── Collapse ─────────────────────────────────────────────────────

    toggleModuleCollapsed(wcName, moduleName) {
      const key = `${wcName}|${moduleName}`;
      this.$set(this.modCollapsed, key, !this.modCollapsed[key]);
    },
    isModuleCollapsed(wcName, moduleName) {
      return !!this.modCollapsed[`${wcName}|${moduleName}`];
    },

    // ── Permission state ──────────────────────────────────────────────

    setPermission(featureCode, level) {
      this.$set(this.permState, featureCode, level);
    },

    // ── Sidebar aggregate ─────────────────────────────────────────────

    wcSidebarValue(wc) {
      const features = wc.Modules.flatMap((m) => m.Features).filter(
        (f) => !f.IsGlobal,
      );
      if (!features.length) return false;
      const withAccess = features.filter(
        (f) => this.permState[f.FeatureCode] !== "none",
      ).length;
      if (withAccess === 0) return false;
      if (withAccess === features.length) return true;
      return null;
    },

    onWcSidebarToggle(wc, event) {
      if (!event.event) return;
      const selecting = this.wcSidebarValue(wc) !== true;
      for (const mod of wc.Modules)
        for (const feat of mod.Features)
          if (!feat.IsGlobal)
            this.$set(
              this.permState,
              feat.FeatureCode,
              selecting ? (feat.HasReadWritePermission ? "rw" : "ro") : "none",
            );
    },

    // ── Module aggregate ──────────────────────────────────────────────

    modAggValue(mod, level) {
      const features = mod.Features.filter(
        (f) => !f.IsGlobal && (level !== "rw" || f.HasReadWritePermission),
      );
      if (!features.length) return false;
      const matched = features.filter(
        (f) => (this.permState[f.FeatureCode] || "none") === level,
      ).length;
      if (matched === 0) return false;
      if (matched === features.length) return true;
      return null;
    },

    onModAggToggle(mod, level, event) {
      if (!event.event) return;
      const newVal = this.modAggValue(mod, level) !== true ? level : "none";
      for (const feat of mod.Features) {
        if (feat.IsGlobal) continue;
        if (level === "rw" && !feat.HasReadWritePermission) continue;
        this.$set(this.permState, feat.FeatureCode, newVal);
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped lang="scss">
// ── Role metadata edit form ───────────────────────────────────────────
.rp-meta-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.rp-meta-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
  flex: 1;

  &--toggle {
    flex: 0 0 auto;
    min-width: 120px;
  }
}

.rp-meta-label {
  font-size: 12px;
  color: #555;
  font-weight: 500;
}

.rp-meta-required {
  color: #d32f2f;
}

.rp-status-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
}

.rp-status-label {
  font-size: 13px;
  color: #333;
}

// ── Layout — mirrors .fa-layout in feature-availability exactly ────────
.rp-layout {
  display: flex;
  height: 100%;
  overflow: hidden;
}

// ── Left sidebar ──────────────────────────────────────────────────────
.rp-sidebar {
  width: 180px;
  min-width: 180px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  background: #fff;
  padding: 12px 0;
}

.rp-sidebar-title {
  padding: 0 16px 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #999;
}

.rp-wc-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f5f5f5;
  }

  &--active {
    background: #e8f0fe;

    .rp-wc-nav-name {
      color: var(--base-accent);
      font-weight: 500;
    }
  }
}

.rp-wc-nav-name {
  font-size: 13px;
  line-height: 1.4;
}

// ── Right content ─────────────────────────────────────────────────────
.rp-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f9f9f9;
}

// ── Toolbar info area ─────────────────────────────────────────────────
.rp-toolbar-label {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rp-toolbar-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.rp-toolbar-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #555;
  max-width: 480px;
  overflow: hidden;
}

.rp-toolbar-sep {
  color: #ccc;
  font-weight: 300;
}

.rp-locked-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  background: #fff3e0;
  color: #e65100;
  white-space: nowrap;

  .dx-icon-lock {
    font-size: 12px;
  }
}

.rp-toolbar-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// ── Search bar ────────────────────────────────────────────────────────
.rp-search-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  .dx-textbox {
    width: 280px;
  }
}

// ── 2-column WC card grid ─────────────────────────────────────────────
.rp-wc-grid {
  columns: 2;
  column-gap: 16px;
}

// ── WC card ───────────────────────────────────────────────────────────
.rp-wc-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 12px;
  overflow: hidden;
  break-inside: avoid;
}

.rp-chevron {
  font-size: 11px;
  color: #666;
}

// ── Column header row ─────────────────────────────────────────────────
.rp-col-header-row {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.rp-col-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: #888;
  text-align: center;
}

// ── Module header ─────────────────────────────────────────────────────
.rp-mod-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f5f5f5;
  }

  &--card {
    padding: 10px 12px;
    background: #f0f4ff;
    border-bottom: 1px solid #dde5ff;

    &:hover {
      background: #e4ecff;
    }
  }
}

.rp-mod-title {
  font-size: 12px;
  font-weight: 600;
}

.rp-agg-inline {
  display: flex;
  margin-left: auto;
}

// ── Feature row ───────────────────────────────────────────────────────
.rp-col-feature {
  flex: 1;
  font-size: 12px;
  padding-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rp-col-check {
  width: 52px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.rp-feature-row {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &--global {
    background: #f9fff5;
  }
}

.rp-global-badge {
  display: inline-block;
  margin-left: 5px;
  padding: 1px 5px;
  font-size: 9px;
  background: #e8f5e9;
  color: #388e3c;
  border-radius: 8px;
  vertical-align: middle;
}

.rp-global-lock {
  font-size: 10px;
  color: #4caf50;
  font-weight: 600;
  text-align: center;
  display: block;
  width: 100%;
}

.rp-no-option {
  display: block;
  width: 16px;
  height: 16px;
}

// ── Empty state ───────────────────────────────────────────────────────
.rp-empty {
  text-align: center;
  padding: 48px;
  color: #999;
  font-size: 14px;
}
</style>
